import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ClipboardList, Search, UserCheck } from "lucide-react";
import { openCalendly } from "@/integrations/calendly";

const stages = [
  { name: "Capture", question: "Can buyers and sellers easily raise their hand?" },
  { name: "Respond", question: "Does every new inquiry get a fast, reliable first response?" },
  { name: "Qualify", question: "Do you know which leads are ready to move — and which need nurturing?" },
  { name: "Follow Up", question: "What happens when a prospect isn't ready immediately?" },
  { name: "Book", question: "Can interested prospects easily book a showing or consultation?" },
  { name: "Track", question: "Can you see what happened to every lead?" },
];

const steps = [
  {
    icon: ClipboardList,
    title: "Tell Us About Your Business",
    desc: "Answer a few simple questions. No complicated questionnaire.",
  },
  {
    icon: Search,
    title: "We Review Your Lead Flow",
    desc: "Our system reviews available public signals and combines them with the information you provide.",
  },
  {
    icon: UserCheck,
    title: "iVIBES Reviews the Findings",
    desc: "Every audit is personally reviewed before it is delivered.",
  },
];

export default function AuditPromo() {
  return (
    <section id="audit" data-testid="audit-promo-section" className="py-20 md:py-28 bg-[#0D1220]">
      <div className="max-w-5xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">
            Where Are Your Leads <span className="text-gradient">Falling Through the Cracks?</span>
          </h2>
          <p className="mt-5 text-lg md:text-xl text-slate-200 leading-relaxed">
            Most agents are losing winnable opportunities between first inquiry and first
            appointment — often without realizing it.
          </p>
          <p className="mt-4 text-base md:text-lg text-slate-300 leading-relaxed">
            The Free iVIBES Real Estate Lead Flow Audit™ reviews how buyer and seller leads move
            from first inquiry toward booked appointments and highlights the areas most likely to
            deserve attention.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-2 md:grid-cols-3 gap-4"
          data-testid="audit-six-stages"
        >
          {stages.map((s, i) => (
            <div key={s.name} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <div className="flex items-center gap-2.5 mb-2.5">
                <span className="h-6 w-6 shrink-0 rounded-full bg-cyan-500/15 text-cyan-300 text-xs font-bold flex items-center justify-center">
                  {i + 1}
                </span>
                <h3 className="font-heading font-bold text-white">{s.name}</h3>
              </div>
              <p className="text-sm text-slate-300 leading-relaxed">{s.question}</p>
            </div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="mt-12 grid sm:grid-cols-3 gap-8"
          data-testid="audit-process"
        >
          {steps.map((s, i) => (
            <div key={s.title} className="flex sm:flex-col items-start gap-4">
              <div className="h-11 w-11 shrink-0 rounded-xl bg-indigo-500/15 text-indigo-300 flex items-center justify-center">
                <s.icon size={21} strokeWidth={1.75} />
              </div>
              <div>
                <h3 className="font-heading font-bold text-white">
                  {i + 1}. {s.title}
                </h3>
                <p className="mt-1.5 text-sm md:text-base text-slate-300 leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.15 }}
          className="mt-12"
        >
          <p
            data-testid="audit-reassurance"
            className="border-l-2 border-cyan-400 pl-4 text-base md:text-lg text-slate-200 font-medium max-w-xl"
          >
            No instant AI score. No fabricated numbers. If something can't be verified, we'll say so.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row sm:items-center gap-4">
            <div>
              <Link
                to="/lead-flow-audit"
                data-testid="audit-promo-cta-button"
                data-track="homepage_lead_flow_audit_clicked"
                className="group inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 bg-indigo-600 hover:bg-indigo-500 font-semibold text-white text-lg transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-[0_0_28px_rgba(99,102,241,0.5)]"
              >
                Get Your Free Real Estate Lead Flow Audit
                <ArrowRight size={19} className="transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <p className="mt-3 text-sm text-slate-400">
                Takes about 1–2 minutes to request. Reviewed findings are usually delivered within
                24 hours — most audits are completed the same business day.
              </p>
            </div>
            <button
              type="button"
              onClick={() => openCalendly()}
              data-testid="audit-promo-review-button"
              data-track="homepage_lead_flow_review_clicked"
              className="inline-flex items-center justify-center rounded-full px-8 py-4 border-2 border-white/25 hover:border-cyan-400/60 hover:text-cyan-300 font-semibold text-lg transition-[border-color,color,transform] duration-200 hover:-translate-y-1"
            >
              Book Your Lead Flow Review
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
